// src/pages/ChatbotPage.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import ChatResponseRenderer from "../components/Common/ChatResponseRenderer";
import ChatbotPaymentModal from "../components/Common/ChatbotPaymentModal";
import config from "../config";
import "./ChatbotPage.css";

const API_BASE_URL = config.API_BASE_URL;

const SESSION_STORAGE_KEY = "chatbot_session_id";

const GREETING = `Hey, I'm Your Scout and welcome to your new secret weapon for reselling.

I built this assistant for one reason: to help resellers like you stop guessing and start making money. Whether you've spotted something at a car boot sale, a charity shop, or online, just drop me a picture or a description and I'll get to work.

Here's what I'll break down for you:

- **Resale value** — what the product is actually worth on the open market
- **Your margin** — profit potential after platform fees are taken out
- **Where to sell it** — the best marketplaces to flip it fast and for the most money
- **Condition rating** — an honest assessment of what you've got in front of you
- **Authenticity check** — a rough guide on whether it's the real deal or one to walk away from

The more information you give me — photos, brand names, condition details, where you found it — the sharper my advice will be.

So, what have you got for me today?`;

function makeSessionId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `session-${Date.now()}`;
}

function saveImgCache(sid, previewUrl) {
  try {
    const list = JSON.parse(sessionStorage.getItem(`chat_imgs_${sid}`) || "[]");
    list.push(previewUrl);
    sessionStorage.setItem(`chat_imgs_${sid}`, JSON.stringify(list));
  } catch (_) {}
}

function getImgCache(sid) {
  try {
    return JSON.parse(sessionStorage.getItem(`chat_imgs_${sid}`) || "[]");
  } catch (_) {
    return [];
  }
}

const TypingIndicator = () => (
  <div className="chatbot-message model">
    <div className="chatbot-message-text-wrap">
      <div className="chatbot-message-text">
        <span className="typing-dots"><span /><span /><span /></span>
      </div>
    </div>
  </div>
);

const LoginRequiredModal = ({ isOpen, onClose, onLogin }) => {
  if (!isOpen) return null;
  return (
    <div className="chatbot-login-modal-overlay">
      <div className="chatbot-login-modal">
        <h3>🔒 Free Limit Reached</h3>
        <p>You've used all <strong>3 free messages</strong> from this device today. Please login to continue chatting unlimited.</p>
        <div className="chatbot-login-modal-buttons">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={onLogin}>Login</button>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ isLimitReached, onSuggestion }) => (
  <div className="chatbot-empty-state">
    <div className="chatbot-empty-logo">T</div>
    <h1 className="chatbot-empty-title">Where should we begin?</h1>
    <div className="chatbot-suggestions">
      {[
        "I found a Nike Air Max at a boot sale — is it worth buying?",
        "What's a PlayStation 4 worth to resell right now?",
        "I have a vintage Levi's jacket, how much can I sell it for?",
        "Check if this Supreme hoodie looks authentic",
      ].map((s) => (
        <button
          key={s}
          className="chatbot-suggestion-card"
          onClick={() => !isLimitReached && onSuggestion(s)}
          disabled={isLimitReached}
        >
          {s}
        </button>
      ))}
    </div>
  </div>
);

const ChatbotPage = () => {
  const { companyId, isAuthenticated, user } = useAuth();
  const [mode] = useState("general");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [toolsUsed, setToolsUsed] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [deviceChatCount, setDeviceChatCount] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [remainingMessages, setRemainingMessages] = useState(3);
  const [userMessageCount, setUserMessageCount] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [userLimitRemaining, setUserLimitRemaining] = useState(10);
  const [userHasUnlimited, setUserHasUnlimited] = useState(false);
  const [loading, setLoading] = useState(true);
  const listRef = useRef(null);
  const restoredRef = useRef(false);
  const isGuest = !isAuthenticated();

  // Track previous counts to detect limit‑hit transition
  const prevGuestCount = useRef(deviceChatCount);
  const prevUserCount = useRef(userMessageCount);

  const handleOverlayClick = () => setSidebarOpen(false);

  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };

  const refreshGuestCount = useCallback(async () => {
    if (!isGuest) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/guest/check-limit`);
      const data = await response.json();
      setDeviceChatCount(data.count);
      setRemainingMessages(data.remaining);
      return data;
    } catch (error) {
      console.error("Failed to refresh guest count:", error);
      return null;
    }
  }, [isGuest]);

  // Initialize device tracking from backend
  useEffect(() => {
    const initDeviceTracking = async () => {
      if (!isGuest) {
        setLoading(false);
        if (isAuthenticated() && companyId) {
          try {
            const response = await api.get(`/api/chat/${companyId}/check-limit`);
            const data = response.data;
            setUserMessageCount(data.count || 0);
            setUserLimitRemaining(data.remaining || 10);
            setUserHasUnlimited(data.unlimited || false);
            if (!data.canChat && !data.unlimited) {
              setShowPaymentModal(true);
            }
          } catch (error) {
            console.error("Failed to check user limit:", error);
          }
        }
        return;
      }

      // GUEST USER
      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/guest/check-limit`);
        const data = await response.json();
        setDeviceChatCount(data.count);
        setRemainingMessages(data.remaining);
        if (!data.canChat && isGuest) {
          setShowLoginModal(true);
        }
      } catch (error) {
        console.error("Failed to init guest tracking:", error);
      } finally {
        setLoading(false);
      }
    };
    initDeviceTracking();
  }, [isGuest, isAuthenticated, companyId]);

  // 🔥 AUTO‑TRIGGER: show modals exactly when limit is hit
  useEffect(() => {
    if (isGuest && prevGuestCount.current < 3 && deviceChatCount >= 3 && !showLoginModal) {
      setShowLoginModal(true);
    }
    prevGuestCount.current = deviceChatCount;
  }, [isGuest, deviceChatCount, showLoginModal]);

  useEffect(() => {
    if (
      !isGuest &&
      !userHasUnlimited &&
      prevUserCount.current < 10 &&
      userMessageCount >= 10 &&
      !showPaymentModal
    ) {
      setShowPaymentModal(true);
    }
    prevUserCount.current = userMessageCount;
  }, [isGuest, userHasUnlimited, userMessageCount, showPaymentModal]);

  // ✅ NEW: If user just became authenticated, dismiss any open guest login modal
  useEffect(() => {
    if (!isGuest && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [isGuest, showLoginModal]);

  // Session management
  useEffect(() => {
    if (loading) return;
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      setSessionId(existing);
      restoredRef.current = true;
    } else {
      const id = makeSessionId();
      setSessionId(id);
      localStorage.setItem(SESSION_STORAGE_KEY, id);
      restoredRef.current = true;
    }
  }, [loading]);

  // Load existing messages for authenticated users
  useEffect(() => {
    if (!isAuthenticated()) return;
    if (!restoredRef.current || !sessionId || !companyId) return;
    restoredRef.current = false;
    api.get(`/api/chat/${companyId}/sessions/${sessionId}`)
      .then((res) => {
        if (res?.data?.success && res.data.messages.length > 0) {
          const imgCache = getImgCache(sessionId);
          let imgIdx = 0;
          const loaded = res.data.messages.map((m) => {
            const msg = { role: m.role, text: m.message || "", hasImage: Boolean(m.has_image), createdAt: m.created_at };
            if (m.has_image && imgCache[imgIdx]) msg.imagePreview = imgCache[imgIdx++];
            return msg;
          });
          if (loaded.length > 0) setMessages(loaded);
        }
      })
      .catch(() => {});
  }, [sessionId, companyId, isAuthenticated]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const fetchSessions = useCallback(async () => {
    if (!isAuthenticated() || !companyId) return;
    setLoadingSessions(true);
    try {
      const res = await api.get(`/api/chat/${companyId}/sessions`);
      if (res?.data?.success) setSessions(res.data.sessions || []);
    } catch (_) {}
    setLoadingSessions(false);
  }, [companyId, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated()) fetchSessions();
  }, [fetchSessions, isAuthenticated]);

  const handleNewChat = useCallback(async () => {
    if (isGuest) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/guest/check-limit`);
        const data = await response.json();
        if (!data.canChat) {
          setShowLoginModal(true);
          return;
        }
      } catch (error) {
        console.error("Failed to check guest limit:", error);
        return;
      }
    } else if (isAuthenticated() && companyId) {
      try {
        const response = await api.get(`/api/chat/${companyId}/check-limit`);
        const data = response.data;
        if (!data.canChat && !data.unlimited) {
          setShowPaymentModal(true);
          return;
        }
      } catch (error) {
        console.error("Failed to check user limit:", error);
        return;
      }
    }

    const id = makeSessionId();
    setSessionId(id);
    localStorage.setItem(SESSION_STORAGE_KEY, id);
    setMessages([]);
    setSelectedImage(null);
    setMessage("");
    sessionStorage.removeItem(`chat_imgs_${id}`);
    closeSidebarOnMobile();
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [isGuest, isAuthenticated, companyId]);

  const loadSession = useCallback(async (session) => {
    if (!isAuthenticated()) return;
    try {
      const res = await api.get(`/api/chat/${companyId}/sessions/${session.id}`);
      if (res?.data?.success) {
        const imgCache = getImgCache(session.id);
        let imgIdx = 0;
        const loaded = res.data.messages.map((m) => {
          const msg = { role: m.role, text: m.message || "", hasImage: Boolean(m.has_image), createdAt: m.created_at };
          if (m.has_image && imgCache[imgIdx]) msg.imagePreview = imgCache[imgIdx++];
          return msg;
        });
        setMessages(loaded.length > 0 ? loaded : []);
        setSessionId(session.id);
        localStorage.setItem(SESSION_STORAGE_KEY, session.id);
        setSelectedImage(null);
        setMessage("");
        closeSidebarOnMobile();
      }
    } catch (_) {}
  }, [companyId, isAuthenticated]);

  const isLimitReached = useMemo(() => {
    if (isAuthenticated()) {
      return !userHasUnlimited && userMessageCount >= 10;
    }
    return deviceChatCount >= 3;
  }, [isAuthenticated, userHasUnlimited, userMessageCount, deviceChatCount]);

  const canSend = useMemo(() => {
    if (loading) return false;
    if (isAuthenticated()) {
      if (!userHasUnlimited && userMessageCount >= 10) return false;
      return Boolean(companyId) && Boolean(sessionId) &&
        (message.trim().length > 0 || Boolean(selectedImage)) && !sending;
    }
    return !isLimitReached && Boolean(sessionId) &&
      (message.trim().length > 0 || Boolean(selectedImage)) && !sending;
  }, [isAuthenticated, companyId, sessionId, message, selectedImage, sending, isLimitReached, loading, userHasUnlimited, userMessageCount]);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage({
        base64: e.target.result,
        mimeType: file.type || "image/jpeg",
        previewUrl: e.target.result,
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleRemoveImage = () => setSelectedImage(null);

  const handleSuggestion = (text) => {
    setMessage(text);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const isFirstUserMessage = messages.filter(m => m.role === "user").length === 0;

const handleUpgrade = async (selectedPlan) => {
  setShowPaymentModal(false);

  try {
    const res = await fetch(config.STRIPE_CHECKOUT_SESSION_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        price: selectedPlan.price,            // 29 ya 49
        email: user?.email || "",             // auth se email
        plan: selectedPlan.name,              // "Unlimited" ya "Pro"
        testMode: config.STRIPE_TEST_MODE,
        success_url: `${config.APP_BASE_URL}/dashboard?checkout=success&plan=${selectedPlan.name}`,
        cancel_url: `${config.APP_BASE_URL}/dashboard?checkout=cancel`,
        company_id: companyId,
        addons: [],
        signup_context: "chatbot_limit_reached",
      }),
    });

    const data = await res.json();
    if (data.success && data.url) {
      window.location.href = data.url;   // Stripe Checkout
    } else {
      alert("Payment session create nahi ho saka. Try again.");
    }
  } catch (err) {
    console.error("Stripe error:", err);
    alert("Something went wrong. Please try again.");
  }
};
  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !selectedImage) return;
    if (loading) return;

    if (isGuest) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/guest/check-limit`);
        const data = await response.json();
        if (!data.canChat) {
          setShowLoginModal(true);
          return;
        }
      } catch (error) {
        console.error("Failed to check guest limit:", error);
        return;
      }
    } else if (isAuthenticated() && companyId) {
      try {
        const response = await api.get(`/api/chat/${companyId}/check-limit`);
        const data = response.data;
        if (!data.canChat && !data.unlimited) {
          setShowPaymentModal(true);
          return;
        }
      } catch (error) {
        console.error("Failed to check user limit:", error);
        return;
      }
    }

    if (!sessionId || sending) return;

    const imageToSend = selectedImage;
    const userMessage = {
      role: "user",
      text: trimmedMessage,
      imagePreview: selectedImage ? selectedImage.previewUrl : null,
      createdAt: new Date().toISOString(),
    };

    if (imageToSend?.previewUrl) saveImgCache(sessionId, imageToSend.previewUrl);

    if (isFirstUserMessage) {
      setMessages([
        userMessage,
        { role: "model", text: GREETING, createdAt: new Date().toISOString() },
      ]);
      setMessage("");
      setSelectedImage(null);

      if (isGuest) {
        try {
          await fetch(`${API_BASE_URL}/api/chat/guest/increment-count`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          await refreshGuestCount();
        } catch (_) {}
      } else if (isAuthenticated() && companyId) {
        try {
          await api.get(`/api/chat/${companyId}/check-limit`);
        } catch (_) {}
      }
      return;
    }

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setSelectedImage(null);
    setSending(true);

    try {
      let response;

      if (isGuest) {
        const fetchResponse = await fetch(`${API_BASE_URL}/api/chat/guest`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: trimmedMessage,
            session_id: sessionId,
            mode,
            image: imageToSend ? imageToSend.base64 : undefined,
            mimeType: imageToSend ? imageToSend.mimeType : undefined,
          }),
        });
        const data = await fetchResponse.json();
        response = { data: data };

        if (data?.success) {
          await refreshGuestCount();
        }
      } else {
        response = await api.post(`/api/chat/${companyId}`, {
          message: trimmedMessage,
          session_id: sessionId,
          mode,
          image: imageToSend ? imageToSend.base64 : undefined,
          mimeType: imageToSend ? imageToSend.mimeType : undefined,
        });

        if (response?.data?.success) {
          try {
            const limitRes = await api.get(`/api/chat/${companyId}/check-limit`);
            const limitData = limitRes.data;
            setUserMessageCount(limitData.count);
            setUserLimitRemaining(limitData.remaining);
            setUserHasUnlimited(limitData.unlimited || false);
          } catch (_) {}
        }
      }

      if (response?.data?.success) {
        setMessages((prev) => [
          ...prev,
          { role: "model", text: response.data.reply || "No reply received.", createdAt: new Date().toISOString() },
        ]);
        setToolsUsed(Array.isArray(response.data.toolsUsed) ? response.data.toolsUsed : []);
        if (!isGuest) fetchSessions();
      } else {
        if (response?.data?.message?.includes("Guest limit reached")) {
          await refreshGuestCount();
          setShowLoginModal(true);
        }
        if (response?.data?.requiresUpgrade) {
          setShowPaymentModal(true);
        }
        setMessages((prev) => [
          ...prev,
          { role: "model", text: response?.data?.message || "I couldn't process that request right now. Please try again.", createdAt: new Date().toISOString() },
        ]);
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.message || error?.message;
      if (serverMsg?.includes("Guest limit reached")) {
        await refreshGuestCount();
        setShowLoginModal(true);
      }
      if (error?.response?.data?.requiresUpgrade) {
        setShowPaymentModal(true);
      }
      setMessages((prev) => [
        ...prev,
        { role: "model", text: serverMsg || "Chat service is unavailable right now. Please try again shortly.", createdAt: new Date().toISOString() },
      ]);
      setToolsUsed([]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const handleLoginRedirect = () => {
    setShowLoginModal(false);
    localStorage.setItem('chatbot_source', 'true');
    window.location.href = "/login?from=chatbot";
  };

  if (loading) {
    return (
      <div className="chatbot-page" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Loading...</div>
      </div>
    );
  }

  const hasMessages = messages.length > 0;

  return (
    <div className="chatbot-page" style={{ height: '100%', maxHeight: '100%' }}>
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLoginRedirect}
      />

      <ChatbotPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onUpgrade={handleUpgrade}
        messageCount={userMessageCount}
      />

      <button
        type="button"
        className="chatbot-mobile-menu-btn"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open chat history"
      >
        ☰
      </button>

      <div
        className={`chatbot-sidebar-overlay ${sidebarOpen ? 'overlay-visible' : ''}`}
        onClick={handleOverlayClick}
      />

      <aside className={`chatbot-history-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <button type="button" className="chatbot-new-chat-btn" onClick={handleNewChat}>
          + New Chat
        </button>

        {isGuest && (
          <div className="chatbot-sidebar-guest">
            <div className="chatbot-guest-dots">
              {[0, 1, 2].map(i => (
                <div key={i} className={`chatbot-guest-dot ${i < deviceChatCount ? 'used' : ''}`} />
              ))}
            </div>
            <span className="chatbot-guest-label">
              {isLimitReached
                ? "Free limit reached (3/3)"
                : `${deviceChatCount}/3 free messages used today`}
            </span>
            <button className="chatbot-guest-login-btn" onClick={handleLoginRedirect}>
              Login for unlimited →
            </button>
          </div>
        )}

        {isAuthenticated() && (
          <>
            <div className="chatbot-history-label">Recent Chats</div>
            <div className="chatbot-session-list">
              {loadingSessions ? (
                <p className="chatbot-no-sessions">Loading...</p>
              ) : sessions.length === 0 ? (
                <p className="chatbot-no-sessions">No history yet.</p>
              ) : (
                sessions.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    className={`chatbot-session-item ${sessionId === s.id ? "active" : ""}`}
                    onClick={() => loadSession(s)}
                    title={s.title}
                  >
                    <span className="chatbot-session-item-title">{s.title}</span>
                    <span className="chatbot-session-item-date">{formatDate(s.updated_at)}</span>
                  </button>
                ))
              )}
            </div>

            {!userHasUnlimited && (
              <div className="chatbot-sidebar-guest" style={{ marginTop: 'auto', marginBottom: '1rem' }}>
                <div className="chatbot-guest-dots">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                    <div key={i} className={`chatbot-guest-dot ${i < userMessageCount ? 'used' : ''}`} style={{ width: '5px', height: '5px' }} />
                  ))}
                </div>
                <span className="chatbot-guest-label">
                  {userMessageCount >= 10
                    ? "Daily limit reached (10/10)"
                    : `${userMessageCount}/10 messages used today`}
                </span>
                {userMessageCount >= 8 && userMessageCount < 10 && (
                  <button className="chatbot-guest-login-btn" onClick={handleUpgrade}>
                    Upgrade for unlimited →
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </aside>

      <div className="chatbot-main">
        <div className="chatbot-message-list" ref={listRef}>
          {!hasMessages ? (
            <EmptyState isLimitReached={isLimitReached} onSuggestion={handleSuggestion} />
          ) : (
            <div className="chatbot-messages-inner">
              {messages.map((item, index) => (
                <div key={`${item.role}-${index}`} className={`chatbot-message ${item.role}`}>
                  {item.role === "model" ? (
                    <div className="chatbot-message-text-wrap">
                      <div className="chatbot-message-text">
                        {item.imagePreview && (
                          <img src={item.imagePreview} alt="uploaded" className="chatbot-message-image" />
                        )}
                        {item.hasImage && !item.imagePreview && (
                          <div className="chatbot-message-image-placeholder">Image was shared</div>
                        )}
                        {item.text && <ChatResponseRenderer text={item.text} role={item.role} />}
                      </div>
                    </div>
                  ) : (
                    <>
                      {item.imagePreview && (
                        <img src={item.imagePreview} alt="uploaded" className="chatbot-message-image" />
                      )}
                      {item.text && (
                        <div className="chatbot-message-text">
                          <ChatResponseRenderer text={item.text} role={item.role} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {sending && <TypingIndicator />}
            </div>
          )}
        </div>

        {toolsUsed.length > 0 && (
          <div className="chatbot-tools-used">
            <span>Tools used:</span>
            <div>{toolsUsed.join(", ")}</div>
          </div>
        )}

        <div className="chatbot-input-wrap">
          {selectedImage && (
            <div className="chatbot-image-preview-wrap">
              <img src={selectedImage.previewUrl} alt={selectedImage.name} className="chatbot-image-preview" />
              <button type="button" className="chatbot-image-remove" onClick={handleRemoveImage} title="Remove image">
                &times;
              </button>
            </div>
          )}
          <div className="chatbot-input-row">
            <textarea
              ref={textareaRef}
              placeholder={isLimitReached ? "Limit reached. Please upgrade to continue." : "Ask me anything..."}
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={sending || isLimitReached}
            />
            <div className="chatbot-input-actions">
              <button
                type="button"
                className="chatbot-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || isLimitReached}
                title="Attach image"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M21.44 11.05L12.25 20.24C9.81 22.68 5.85 22.68 3.41 20.24C0.97 17.8 0.97 13.84 3.41 11.4L12.6 2.21C14.22 0.59 16.85 0.59 18.47 2.21C20.09 3.83 20.09 6.46 18.47 8.08L9.63 16.92C8.82 17.73 7.5 17.73 6.69 16.92C5.88 16.11 5.88 14.79 6.69 13.98L14.82 5.85" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageSelect}
              />
              <button
                type="button"
                className="chatbot-send-btn"
                onClick={handleSend}
                disabled={!canSend}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotPage;